'use client';

import Image from 'next/image';
import type { Product, ProductVariant } from '@/lib/shopify/types';

interface VariantSelectorProps {
  product: Product;
  selectedVariant: ProductVariant | null;
  onVariantChange: (variant: ProductVariant) => void;
  hiddenOptionNames?: string[];
}

// Shopify option naming varies — some products call this option "Finish",
// others "Variant". Treat both as the finish option for ordering, labelling
// and the embroidery tooltip.
const isFinishOptionName = (name: string) => {
  const n = name.toLowerCase();
  return n === 'finish' || n === 'variant';
};

const imageMatchesExactColourSequence = (url: string, colour: string) => {
  const tokens = (value: string) => value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const colourSequence = tokens(colour).join(' ');
  const filename = decodeURIComponent(url.split('/').pop()?.split('?')[0] || '');
  return colourSequence !== '' && tokens(filename).join(' ').includes(colourSequence);
};

export default function VariantSelector({
  product,
  selectedVariant,
  onVariantChange,
  hiddenOptionNames = [],
}: VariantSelectorProps) {
  const hiddenOptionSet = new Set(hiddenOptionNames.map((name) => name.toLowerCase()));
  const isHiddenOption = (name: string) => hiddenOptionSet.has(name.toLowerCase());
  const visualVariant = selectedVariant || product.variants.find((v) => v.availableForSale) || product.variants[0] || null;
  const selectedOptions: Record<string, string> = {};
  visualVariant?.selectedOptions.forEach((opt) => {
    selectedOptions[opt.name] = opt.value;
  });

  const handleOptionChange = (optionName: string, value: string) => {
    const newOptions = { ...selectedOptions, [optionName]: value };

    // Find the matching variant
    const matchesVisibleOptions = (variant: ProductVariant) =>
      variant.selectedOptions.every((opt) =>
        isHiddenOption(opt.name) || newOptions[opt.name] === opt.value
      );

    const matchingVariant =
      product.variants.find((variant) => variant.availableForSale && matchesVisibleOptions(variant)) ||
      product.variants.find(matchesVisibleOptions);

    if (matchingVariant) {
      onVariantChange(matchingVariant);
    }
  };

  // Check if a specific option combination is available
  const isOptionAvailable = (optionName: string, value: string): boolean => {
    const testOptions = { ...selectedOptions, [optionName]: value };
    return product.variants.some(
      (variant) =>
        variant.availableForSale &&
        variant.selectedOptions.every((opt) =>
          isHiddenOption(opt.name) || testOptions[opt.name] === opt.value
        )
    );
  };

  const OPTION_PRIORITY: Record<string, number> = {
    finish: 0,
    variant: 0,
    color: 1,
    colour: 1,
    size: 2,
  };
  const sortedOptions = [...product.options].sort((a, b) => {
    const ap = OPTION_PRIORITY[a.name.toLowerCase()] ?? 99;
    const bp = OPTION_PRIORITY[b.name.toLowerCase()] ?? 99;
    return ap - bp;
  });

  return (
    <div className="space-y-6">
      {sortedOptions.map((option) => {
        const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
        const isSizeOption = option.name.toLowerCase() === 'size';

        if (isHiddenOption(option.name)) return null;

        return (
          <div key={option.id}>
            {/* Option Label */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium uppercase tracking-wider">
                {isColorOption || isFinishOptionName(option.name) ? `Select a ${option.name}` : option.name}
                {selectedOptions[option.name] && (
                  <span className="font-normal text-[var(--color-text-secondary)] ml-2">
                    {selectedOptions[option.name]}
                  </span>
                )}
              </span>
              {isSizeOption && (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('open-size-guide'))}
                  className="text-xs text-[var(--color-text-secondary)] underline hover:text-black transition-colors"
                >
                  Size Guide
                </button>
              )}
            </div>

            {/* Option Values */}
            {isColorOption ? (
              // Color variant image thumbnails
              <div className="flex flex-wrap gap-3">
                {option.values.map((value) => {
                  const isSelected = selectedOptions[option.name] === value;
                  const isAvailable = isOptionAvailable(option.name, value);
                  // Prefer a variant whose image filename matches the colour
                  // tokens — Shopify variant images for non-primary Finish
                  // combos are often mis-assigned to the wrong colour image
                  // (commonly Black). Falls back to first-with-image.
                  const candidates = product.variants.filter(
                    (v) => v.image && v.selectedOptions.some(
                      (opt) => opt.name === option.name && opt.value === value
                    )
                  );
                  const tokens = value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
                  const variantWithImage =
                    (tokens.length
                      ? candidates.find((v) => (
                          v.image && imageMatchesExactColourSequence(v.image.url, value)
                        )) || candidates.find((v) => {
                          const url = (v.image?.url || '').toLowerCase();
                          return tokens.every((t) => url.includes(t));
                        })
                      : undefined) || candidates[0];
                  const thumbUrl = variantWithImage?.image?.url;

                  return (
                    <div key={value} className="flex flex-col items-center gap-1 w-20">
                      <button
                        onClick={() => handleOptionChange(option.name, value)}
                        disabled={!isAvailable}
                        title={value}
                        className={`relative w-20 h-20 overflow-hidden border-2 transition-all ${
                          isSelected
                            ? 'border-brand-blue'
                            : 'border-[var(--color-border)] hover:border-gray-400'
                        } ${!isAvailable ? 'opacity-30 cursor-not-allowed' : ''}`}
                      >
                        {thumbUrl ? (
                          <Image
                            src={thumbUrl}
                            alt={value}
                            width={80}
                            height={80}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="flex items-center justify-center w-full h-full bg-gray-100 text-[10px] text-gray-500">
                            {value.slice(0, 3)}
                          </span>
                        )}
                        {!isAvailable && (
                          <span className="absolute inset-0 flex items-center justify-center bg-white/50">
                            <span className="w-full h-[1px] bg-gray-400 rotate-45 absolute" />
                          </span>
                        )}
                      </button>
                      <span className={`text-[10px] leading-tight text-center ${isSelected ? 'font-semibold text-black' : 'text-gray-500'}`}>
                        {value}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : isSizeOption ? (
              // Size dropdown
              <div className="relative">
                <select
                  value={selectedOptions[option.name] || ''}
                  onChange={(e) => handleOptionChange(option.name, e.target.value)}
                  className="w-full appearance-none rounded-none border border-[var(--color-border)] bg-white px-4 py-3 pr-10 text-sm font-medium text-black cursor-pointer hover:border-black focus:border-black focus-visible:outline-none transition-colors"
                >
                  <option value="" disabled>Select a size</option>
                  {option.values.map((value) => {
                    const isAvailable = isOptionAvailable(option.name, value);
                    return (
                      <option key={value} value={value} disabled={!isAvailable}>
                        {value}{!isAvailable ? ' — Out of Stock' : ''}
                      </option>
                    );
                  })}
                </select>
                <svg
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
            ) : (
              // Other option buttons
              <div className="flex flex-wrap gap-2">
                {option.values.map((value) => {
                  const isSelected = selectedOptions[option.name] === value;
                  const isAvailable = isOptionAvailable(option.name, value);

                  // For embroidered Finish options, surface the digitising-fee
                  // heads-up on hover so customers see it before they commit.
                  const v = value.toLowerCase();
                  const isFinishOption = isFinishOptionName(option.name);
                  let tooltip: string | undefined;
                  if (isFinishOption && v.includes('embroider')) {
                    const hasFront = v.includes('front') || v.includes('left');
                    const hasBack = v.includes('back') || v.includes('right');
                    if (hasFront && hasBack) tooltip = 'Includes a one-time £55 digitising fee (£25 front + £30 back). Charged once per design.';
                    else if (hasBack) tooltip = 'Includes a one-time £30 digitising fee. Charged once per design.';
                    else tooltip = 'Includes a one-time £25 digitising fee. Charged once per design.';
                  }

                  return (
                    <button
                      key={value}
                      onClick={() => handleOptionChange(option.name, value)}
                      disabled={!isAvailable}
                      title={tooltip}
                      className={`min-w-[48px] px-3 py-2 text-sm font-medium border transition-all ${
                        isSelected
                          ? 'bg-brand-blue text-white border-brand-blue'
                          : isAvailable
                          ? 'bg-white text-black border-[var(--color-border)] hover:border-black'
                          : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
